import { TeamWith } from "@redotech/account-common/base-team";
import { GetUser } from "@redotech/account-common/user";
import {
  isMessageChannel,
  messageChannelDisplayName,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/channel-utils";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import { ConversationStatus } from "@redotech/redo-common-model/support/conversation-status";
import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getConversationTagsOfTeam } from "@redotech/redo-merchant-app-common/client/conversation-tags";
import { REDO_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  ConversationMessageType,
  ExpandedConversation,
  MessageVisibility,
  MinimalConversation,
} from "@redotech/redo-support-common/conversation";
import {
  AssigneesFilter,
  AssigneesFilterType,
  ConversationFiltersV3,
  ConversationTagFilterType,
  ConversationTagsFilter,
  FilterGroupFilterOption,
  FiltersStatus,
  MentionsUsersFilter,
} from "@redotech/redo-support-common/conversation-filters/conversation-filters";
import { TagGroup } from "@redotech/redo-support-common/support/tag-group-zod-schema";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import type { SupportSettings } from "@redotech/redo-support-settings-common/support-settings";
import { CloseActionMethod } from "@redotech/redo-support-settings-common/support-settings-misc";
import {
  ConversationTagRecord,
  SPAM_TAG,
} from "@redotech/redo-support-tags-common/conversation-tag-record";
import { SortDirection } from "@redotech/redo-views-common/table";
import { toast } from "@redotech/redo-web/alert";
import { assertNever } from "@redotech/util/type";
import { DragEvent } from "react";
import {
  getMinimalConversation,
  updateConversation,
} from "./client/conversations";

export const getPresetViewFilters = (
  viewName: string,
  user?: GetUser,
): ConversationFiltersV3 | null => {
  const assigneeFilter: AssigneesFilter[] = user
    ? [
        {
          type: FilterGroupFilterOption.ASSIGNEES,
          value: [user._id],
          query: AssigneesFilterType.INCLUDES,
        },
      ]
    : [];

  const mentionsFilter: MentionsUsersFilter[] = user
    ? [{ type: FilterGroupFilterOption.MENTIONS, value: [user._id] }]
    : [];

  const spamFilter: ConversationTagsFilter = {
    type: FilterGroupFilterOption.CONVERSATION_TAGS,
    value: [SPAM_TAG],
    query: ConversationTagFilterType.ANY_OF,
  };

  switch (viewName) {
    case "all":
      return { status: FiltersStatus.OPEN, advancedFilters: [] };
    case "assigned":
      return { advancedFilters: assigneeFilter, status: FiltersStatus.OPEN };
    case "sent":
      return {
        advancedFilters: [],
        sort: { key: "lastMerchantResponse", direction: SortDirection.DESC },
      };
    case "spam":
      return { status: FiltersStatus.OPEN, advancedFilters: [spamFilter] };
    case "mentions":
      return { status: FiltersStatus.OPEN, advancedFilters: mentionsFilter };
    case "drafts":
      return { drafts: true, advancedFilters: [] };
    default:
      return null;
  }
};

export const getConversationActivityStream = ({
  authorization,
  signal,
}: {
  authorization: { Authorization: string };
  signal: AbortSignal;
}) => {
  // See comment in getConversationsStream
  const url = `${REDO_API_URL}/conversations/conversation-activity?rand=${Math.random()}`;
  return fetch(url, { signal, headers: authorization });
};

export const sendConversationActivityUpdate = async ({
  authorization,
  signal,
  activeConversationId,
  prevConversationId,
}: {
  authorization: { Authorization: string };
  signal?: AbortSignal;
  activeConversationId?: string;
  prevConversationId?: string;
}) => {
  const url = `${REDO_API_URL}/conversations/conversation-activity`;
  try {
    await fetch(url, {
      signal,
      method: "POST",
      headers: { "Content-Type": "application/json", ...authorization },
      body: JSON.stringify({ activeConversationId, prevConversationId }),
    });
  } catch (error) {
    // Handle AbortError gracefully - silently ignore when requests are cancelled
    if (error instanceof Error && error.name === "AbortError") {
      return; // Don't throw or log AbortError
    }
    // Re-throw other errors to be handled by calling code
    throw error;
  }
};

export const removeSimpleHTMLTags = (message: string) => {
  const regex = /(<(i|b|u)>)|(<a href=.*?>)|(<\/(i|b|u|a)>)/g;
  return message.replaceAll(regex, "").trim();
};

export const closeTicket = async (
  client: RedoMerchantClient,
  conversation: MinimalConversation,
  setActiveConversation: (val: MinimalConversation | undefined) => void,
  team: TeamWith<{ support?: SupportSettings }>,
  inDetailView?: boolean,
  nextConversation?: MinimalConversation,
): Promise<MinimalConversation | undefined> => {
  const closePromise = updateConversation(client, conversation, {
    status: "closed",
    snoozedUntil: null,
  });
  let newActiveConversation;
  if (inDetailView) {
    const closeAction = team.settings.support?.closeAction;
    if (closeAction) {
      switch (closeAction) {
        case CloseActionMethod.NEXT: {
          if (!nextConversation) {
            // cover the case where there is no next conversation (i.e. this is the only conversation in the inbox)
            newActiveConversation = undefined;
            toast("Last conversation in inbox", { variant: "success" });
            break;
          }
          newActiveConversation = nextConversation;
          break;
        }
        case CloseActionMethod.STAY: {
          newActiveConversation = {
            ...conversation,
            status: ConversationStatus.CLOSED,
          };
          break;
        }
        case CloseActionMethod.TABLE: {
          newActiveConversation = undefined;
          break;
        }
        default: {
          assertNever(closeAction);
        }
      }
    } else {
      // Handle missing or undefined closeAction by defaulting to clearing active conversation
      newActiveConversation = undefined;
    }
    setActiveConversation(newActiveConversation);
  }
  await closePromise;
  return newActiveConversation;
};

export const getNextBatch = async (
  iterator: AsyncIterator<{ conversations: MinimalConversation[] }>,
): Promise<{
  done: boolean;
  value: { conversations: MinimalConversation[] };
}> => {
  let done: boolean;
  let value: { conversations: MinimalConversation[] };
  try {
    const results = await iterator.next();
    done = results.done || false;
    value = results.value;
  } catch {
    done = true;
    value = { conversations: [] };
  }
  if (done && iterator.return) {
    await iterator.return();
  }
  return { done, value };
};

const iterateOverHtmlDoc = (
  element: HTMLHtmlElement | ChildNode | null,
  messagePieces: string[],
) => {
  if (element) {
    const nodeList = element.childNodes;
    if (nodeList?.length) {
      for (const node of nodeList) {
        // Skip style and comment tags
        if (node.nodeName === "STYLE" || node.nodeName === "#comment") {
          continue;
        }
        if (node.nodeType === 3 && node.nodeValue && node.nodeValue.trim()) {
          // Text node
          messagePieces.push(node.nodeValue);
        } else {
          // Element node
          if (node.nodeType === 1) {
            if (node.nodeName === "A") {
              messagePieces.push(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                `<a href="${(node as HTMLAnchorElement).href}">`,
              );
            }
            // Handle elements that always cause line breaks
            if (nonCollapsingElements.includes(node.nodeName)) {
              messagePieces.push("\n(noncollapse)");
            }
          }

          // When divs or other similar elements open, add a line break if there isn't already one
          // Don't add line breaks from divs at the beginning, before any content
          if (
            node.nodeType === 1 &&
            collapsingElements.includes(node.nodeName) &&
            messagePieces.length &&
            messagePieces[messagePieces.length - 1] !== "\n(collapse)" &&
            messagePieces[messagePieces.length - 1] !== "\n(noncollapse)"
          ) {
            messagePieces.push("\n(collapse)");
          }

          iterateOverHtmlDoc(node, messagePieces);
          if (node.nodeType === 1 && node.nodeName === "A") {
            messagePieces.push("</a>");
          }

          // When divs or other similar elements close, add a line break if there isn't already one
          // Don't add line breaks from divs at the beginning, before any content
          if (
            node.nodeType === 1 &&
            collapsingElements.includes(node.nodeName) &&
            messagePieces.length &&
            messagePieces[messagePieces.length - 1] !== "\n(collapse)" &&
            messagePieces[messagePieces.length - 1] !== "\n(noncollapse)"
          ) {
            messagePieces.push("\n(collapse)");
          }
        }
      }
    }
  }
};

const iterateOverHtmlDocForComments = (
  element: HTMLHtmlElement | ChildNode | null,
  messagePieces: string[],
) => {
  if (element) {
    const nodeList = element.childNodes;
    if (nodeList?.length) {
      for (const node of nodeList) {
        if (node.nodeName === "#comment") {
          node.remove();
        } else {
          messagePieces.push(node.nodeValue || "");
        }
        iterateOverHtmlDocForComments(node, messagePieces);
      }
    }
  }
};

const removeCollapseTags = (pieces: string[]) => {
  return pieces.map((piece: string) => {
    if (piece === "\n(collapse)" || piece === "\n(noncollapse)") {
      return "\n";
    } else {
      return piece;
    }
  });
};

export const parseHtml = (htmlText: string) => {
  const doc = new DOMParser().parseFromString(htmlText, "text/html");
  const element = doc.querySelector("html");
  const messagePieces: string[] = [];
  iterateOverHtmlDoc(element, messagePieces);
  const messagePiecesWithCollapseTagsRemoved =
    removeCollapseTags(messagePieces);
  return messagePiecesWithCollapseTagsRemoved.join("").trim();
};

const collapsingElements = [
  "DIV",
  "FOOTER",
  "MAIN",
  "SECTION",
  "ARTICLE",
  "DL",
  "FORM",
  "NAV",
  "TABLE",
  "ASIDE",
  "TFOOT",
  "HEADER",
  "FIGCAPTION",
  "DD",
  "OL",
  "UL",
  "TR",
  "TD",
];

const nonCollapsingElements = [
  "BR",
  "HR",
  "ADDRESS",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "BLOCKQUOTE",
  "P",
  "VIDEO",
  "DT",
  "FIELDSET",
  "LI",
  "PRE",
  "FIGURE",
];

export const doFileDrop = async (
  event: DragEvent<HTMLDivElement>,
  handleUpload: ({
    event,
    file,
  }: {
    event?: any;
    file?: File;
  }) => Promise<void>,
) => {
  const nonImages = Array.from(event.dataTransfer.files).filter(
    (file: File) => {
      return !file.type.startsWith("image/");
    },
  );
  for (const file of nonImages) {
    await handleUpload({ file });
  }
  return;
};

export const removeHtmlComments = (htmlText: string) => {
  const doc = new DOMParser().parseFromString(htmlText, "text/html");
  const element = doc.querySelector("html");
  const messagePieces: string[] = [];
  iterateOverHtmlDocForComments(element, messagePieces);
  return doc.documentElement.innerHTML;
};

export const getLastCustomerDirectMessage = (
  conversation: ExpandedConversation,
) => {
  return conversation.messages
    .slice()
    .reverse()
    .find(
      (message) =>
        message.type === "customer" &&
        ((conversation.platform === ConversationPlatform.INSTAGRAM &&
          !!message.instagram?.messageId) ||
          (conversation.platform === ConversationPlatform.FACEBOOK &&
            !!message.facebook?.messageId)),
    );
};

// Must be a customer DM within 7 days of the last customer message
export const canSendMetaMessage = (conversation: ExpandedConversation) => {
  if (
    ![ConversationPlatform.FACEBOOK, ConversationPlatform.INSTAGRAM].includes(
      conversation.platform,
    )
  ) {
    return false;
  }

  const lastCustomerDirectMessage = getLastCustomerDirectMessage(conversation);
  if (!lastCustomerDirectMessage) {
    return false;
  }

  const lastCustomerResponseAt = new Date(lastCustomerDirectMessage.sentAt);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  return lastCustomerResponseAt > sevenDaysAgo;
};

// Last customer message must be a comment, there must be no (public) merchant messages after it,
// and it must be within 7 days
export const canSendInstagramPrivateReply = (
  conversation: ExpandedConversation,
) => {
  const lastCustomerMessageIndex = conversation.messages
    .map((message) => message.type)
    .lastIndexOf(ConversationMessageType.CUSTOMER);

  if (
    lastCustomerMessageIndex === -1 ||
    conversation.platform !== ConversationPlatform.INSTAGRAM
  ) {
    return false;
  }

  const lastCustomerMessage = conversation.messages[lastCustomerMessageIndex];
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (!lastCustomerMessage.instagram?.comment) {
    return false;
  }

  for (
    let i = lastCustomerMessageIndex + 1;
    i < conversation.messages.length;
    i++
  ) {
    if (
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      conversation.messages[i].type === "merchant" &&
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      conversation.messages[i].visibility !== MessageVisibility.INTERNAL
    ) {
      return false;
    }
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const lastCustomerResponseAt = new Date(lastCustomerMessage.sentAt);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  return lastCustomerResponseAt > sevenDaysAgo;
};

export const getPrivateReplyCommentId = (
  conversation: ExpandedConversation,
): string | null => {
  if (!canSendInstagramPrivateReply(conversation)) {
    return null;
  }

  const lastCustomerMessageIndex = conversation.messages
    .map((message) => message.type)
    .lastIndexOf(ConversationMessageType.CUSTOMER);

  const lastCustomerMessage = conversation.messages[lastCustomerMessageIndex];
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return lastCustomerMessage.instagram?.comment?.commentId || null;
};

/**
 * Cross-version channel label. V2 {@link MessageChannel} keys (the analytics
 * "Tickets by channel" chart, channel filter dropdowns) resolve through the
 * exhaustive {@link messageChannelDisplayName} map; the remaining cases are the
 * legacy V1 {@link ConversationPlatform} keys (`email`/`facebook`/`instagram`)
 * that aren't in the V2 enum but are still passed by surfaces rendering V1 data
 * (merge modal, ticket-channel rule conditions, Concierge dashboard filter).
 */
export const channelDisplayName = (channelName: string): string => {
  if (isMessageChannel(channelName)) {
    return messageChannelDisplayName(channelName);
  }
  switch (channelName) {
    case "email":
      return "Email";
    case "facebook":
      return "Facebook";
    case "instagram":
      return "Instagram DM";
    default:
      return channelName;
  }
};

export const addZwspAfterLink = (html: string) => {
  const zwsp = "\u200B";
  return html.replace(/<\/a>(\u200B)*/g, `</a>${zwsp}`);
};

export const validateConversationCanClose = async (
  rpcClient: RedoMerchantRpcClient,
  conversationId: string,
  teamId: string,
  client: RedoMerchantClient,
): Promise<{ canClose: boolean; missingTagGroups: TagGroup[] }> => {
  try {
    const [tagGroupsResult, conversationTags, conversation] = await Promise.all(
      [
        rpcClient.getTagGroups({}),
        getConversationTagsOfTeam(client, { teamId }),
        getMinimalConversation(client, { conversationId }),
      ],
    );
    const requiredTagGroups = tagGroupsResult.tagGroups.filter(
      (group: TagGroup) => group.requireTagBeforeClose,
    );

    if (requiredTagGroups.length === 0) {
      return { canClose: true, missingTagGroups: [] };
    }

    const missingTagGroups: TagGroup[] = [];
    //find tag group from the tag that is matching within the conversationTags result.
    const conversationTagGroupIds = new Set(
      conversation.tagIds
        ?.map(
          (tag) =>
            conversationTags.find(
              (t: ConversationTagRecord) => t._id === tag.tagId,
            )?.tagGroupId,
        )
        .filter(Boolean) || [],
    );

    for (const requiredGroup of requiredTagGroups) {
      if (!conversationTagGroupIds.has(requiredGroup._id)) {
        missingTagGroups.push(requiredGroup);
      }
    }

    return { canClose: missingTagGroups.length === 0, missingTagGroups };
  } catch (error) {
    console.error("Failed to validate conversation can close:", error);
    return { canClose: true, missingTagGroups: [] };
  }
};
